// Daily Rafiki and challenge links: one fixed deal that everyone gets.
//
// A game with a `seed` deals its cards (and gives the computer players their small random
// choices) from that seed instead of Math.random, so two people opening the same seed see the
// same table. The daily seed is the UTC date; a challenge seed is a short random string.
// Everything here is plain data and pure functions, so it is easy to test; the UI lives in
// Daily.tsx and components/DailyResultCard.tsx.
import seedrandom from "seedrandom";
import { cardScore } from "../common";
import { NOCARD } from "../constants";

import { t } from "../i18n";
import { store } from "../store";
// Daily #1. Earlier dates give a number of 0 or less, which the UI never shows.
export const DAILY_EPOCH = "2026-11-01";
const DAY_MS = 24 * 60 * 60 * 1000;

const utcMidnight = (date: string) => {
  const [y, m, d] = date.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};

// Today's date as "YYYY-MM-DD" in UTC: the day changes at the same moment for everyone.
export const utcDate = (now: Date = new Date()) => now.toISOString().slice(0, 10);

// Daily #N: days since the epoch, counting the epoch day as #1.
export const dailyNumber = (date: string) => Math.round((utcMidnight(date) - utcMidnight(DAILY_EPOCH)) / DAY_MS) + 1;

// "#12" for a daily, or "preview" before the first one goes live.
export const dailyLabel = (number: number) => (number >= 1 ? `#${number}` : t("daily.preview"));

// The shuffle seed for a day. Kept separate from the date so it can change without a new epoch.
export const dailySeed = (date: string) => date;

export const msUntilNextDaily = (now: Date = new Date()) => utcMidnight(utcDate(now)) + DAY_MS - now.getTime();

// "HH:MM" until the next daily.
export const formatCountdown = (ms: number) => {
  const total = Math.max(0, Math.ceil(ms / 60000));
  const h = Math.floor(total / 60);
  const m = total % 60;
  const p = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${p(h)}:${p(m)}`;
};

// A random number source: seeded when a seed is given, otherwise the browser's.
export const rngFor = (seed?: string): (() => number) => (seed ? seedrandom(seed) : Math.random);

// Fisher-Yates, in place, from the given source of randomness. Same seed, same order.
export const shuffleWith = <T>(array: T[], random: () => number = Math.random): T[] => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

// Short, URL-safe seed for a challenge link, e.g. "k3f9ax".
export const randomSeed = () => Math.random().toString(36).slice(2, 8).padEnd(6, "0");

// Computer players for a seeded game get ids derived from the seed: their memory lapses are
// seeded by id (see bot.ts), so the same seed gives the same forgetful opponents.
export const seededBotId = (seed: string, n: number) => `bot-${seed.replace(/[^a-z0-9-]/gi, "")}-${n}`;

// One square per card left in the hand, without giving the cards away:
// green is a card you want (2 points or less), yellow is middling, black is a brick.
export const GREEN = "🟩"; // 🟩
export const YELLOW = "🟨"; // 🟨
export const BLACK = "⬛"; // ⬛

export const cardSquare = (card: string, rules?: string) => {
  const score = cardScore(card, rules);
  if (score <= 2) return GREEN;
  if (score <= 9) return YELLOW;
  return BLACK;
};

export const shareGrid = (hand: string[] | undefined, rules?: string) =>
  (hand || [])
    .filter((card) => card && card !== NOCARD)
    .map((card) => cardSquare(card, rules))
    .join("");

export const DAILY_URL = "playrafiki.com/daily";
export const CHALLENGE_BASE = "https://playrafiki.com/challenge/";

export interface DailyResult {
  date: string; // "YYYY-MM-DD" (UTC)
  number: number; // Daily #N
  score: number; // the player's round score, penalty included
  grid: string; // emoji squares for the final hand
  called: boolean; // the player called RAFIKI
  rank: number; // 1 = lowest score at the table (ties share a rank)
  players: number; // how many were at the table
  seed: string;
}

export const ordinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export const rankLine = (rank: number, players: number) =>
  rank === 1 ? t("daily.beatComputers") : t("daily.rankOf", { ord: ordinal(rank), rank, n: players });

// What the player sends to friends. Portal builds run inside another site and may not link
// out, so they get the grid and the number only.
// Laid out like Wordle's share (title and score, a blank line, the squares), ending with the
// challenge hook Sam liked: "Beat me:" and a link to the same deal with the score to beat.
// Portal builds run inside another site and may not link out: no link there.
export const dailyShareText = (result: DailyResult, _callWord = "RAFIKI", portal = false) => {
  const points = t("common.points", { count: result.score });
  const lines = [`${t("daily.titleN", { label: dailyLabel(result.number) })} · ${points} ${t("daily.lowestWins")}`, "", result.grid];
  if (!portal) {
    const url = challengeUrl(result.seed || dailySeed(result.date), result.score).replace(/^https:\/\//, "");
    lines.push("", t("daily.beatMe", { url }));
  }
  return lines.join("\n");
};

export const challengeUrl = (seed: string, score?: number, name?: string) => {
  const params = new URLSearchParams();
  if (typeof score === "number") params.set("s", String(score));
  if (name) params.set("n", name);
  const q = params.toString();
  return `${CHALLENGE_BASE}${encodeURIComponent(seed)}${q ? `?${q}` : ""}`;
};

export const challengeShareText = (seed: string, score: number, name?: string) =>
  t("challenge.shareText", { score, url: challengeUrl(seed, score, name).replace(/^https:\/\//, "") });

// Who the challenger was, from the link's query string: ?s=7&n=Sam (both optional).
export const parseChallengeQuery = (search: string): { score?: number; name?: string } => {
  const params = new URLSearchParams(search || "");
  const s = params.get("s");
  const n = params.get("n");
  const score = s !== null && s !== "" && /^-?\d+$/.test(s) ? Number(s) : undefined;
  const name = n ? n.slice(0, 24) : undefined;
  return { score, name };
};

// One go per day, remembered on this device.
export const dailyKey = (date: string) => `rafiki.daily.${date}`;

export const readDailyResult = (date: string): DailyResult | null => {
  try {
    const raw = store.getItem(dailyKey(date));
    const r = raw ? JSON.parse(raw) : null;
    if (!r || typeof r.score !== "number" || typeof r.grid !== "string") return null;
    return r as DailyResult;
  } catch (e) {
    return null;
  }
};

export const hasPlayedDaily = (date: string) => readDailyResult(date) !== null;

export const saveDailyResult = (result: DailyResult) => {
  try {
    store.setItem(dailyKey(result.date), JSON.stringify(result));
    return true;
  } catch (e) {
    return false;
  }
};

// The rank of a score among a table's scores (ties share the better rank).
export const rankOf = (score: number, scores: number[]) => scores.filter((s) => s < score).length + 1;

// Analytics: "daily_complete" when the day's game ends.
export const dailyCompleteParams = (result: DailyResult) => ({
  dailyNumber: result.number,
  score: result.score,
  rank: result.rank,
  called: result.called ? "yes" : "no",
});

// Analytics: "challenge_complete" when a challenge link's game ends, with how it went
// against the friend's score (when the link carried one).
export const challengeCompleteParams = (score: number, challengerScore?: number) => ({
  score,
  outcome:
    typeof challengerScore !== "number" ? "no_score" : score < challengerScore ? "win" : score > challengerScore ? "lose" : "tie",
});
